import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import { clearSpeechRateAlert, SpeechRateAlert } from '../store/conversationSlice';
import { useAlertViewer } from '../contexts/SessionViewerContext';
import { isViewersOwnAlert } from '../constants/alertTheme';
import { SPEECH_RATE_BANNER_COPY, SPEECH_RATE_BANNER_HIDE_MS } from '../constants/speechRateBanner';

/**
 * "Speaking too fast" at the top of the live call, for SPEECH_RATE_BANNER_HIDE_MS
 * or until dismissed. It is coaching for the rep who spoke too fast, so it shows
 * ONLY the viewer's own alert (2026-10-05): the alert's `participantId` is the
 * viewer's own participant (a bot call owner's "Me", a web co-seller's
 * `salesperson_<n>`). The recall relay broadcasts every speaker's alert to
 * everyone on the bot call; anyone else's, and every alert a watcher gets, is
 * dropped the moment it arrives. An older service's alert names nobody and
 * stays the call owner's, as before.
 */
const SpeechRateBanner: React.FC = () => {
  const dispatch = useDispatch();
  const viewer = useAlertViewer();
  const lastSpeechRateAlert = useSelector((state: RootState) => state.conversation?.lastSpeechRateAlert);
  // The banner keeps its own copy: another rep's alert arriving in the one
  // store slot must neither replace the viewer's numbers nor hide them early.
  const [shownAlert, setShownAlert] = useState<SpeechRateAlert | null>(null);

  const isOwnAlert =
    !!lastSpeechRateAlert &&
    isViewersOwnAlert({ speaker: 'salesperson', participantId: lastSpeechRateAlert.participantId }, viewer);

  const hide = useCallback(() => {
    setShownAlert(null);
    dispatch(clearSpeechRateAlert());
  }, [dispatch]);

  useEffect(() => {
    if (!lastSpeechRateAlert) return;
    if (isOwnAlert) setShownAlert(lastSpeechRateAlert);
    else dispatch(clearSpeechRateAlert());
  }, [lastSpeechRateAlert, isOwnAlert, dispatch]);

  useEffect(() => {
    if (!shownAlert) return undefined;
    const timer = setTimeout(hide, SPEECH_RATE_BANNER_HIDE_MS);
    return () => clearTimeout(timer);
  }, [shownAlert, hide]);

  if (!shownAlert) return null;

  return (
    <div role="alert" className="bg-error text-white px-6 py-3 flex items-center justify-between">
      <div className="flex items-center space-x-3">
        <div>
          <span className="font-bold">{SPEECH_RATE_BANNER_COPY.title} </span>
          <span>
            {SPEECH_RATE_BANNER_COPY.detail(shownAlert.words_per_minute, shownAlert.wpm_threshold)}
          </span>
        </div>
      </div>
      <button
        onClick={hide}
        className="bg-error hover:opacity-90 px-3 py-1 rounded text-sm transition-colors"
      >
        {SPEECH_RATE_BANNER_COPY.dismiss}
      </button>
    </div>
  );
};

export default SpeechRateBanner;
